import { AddIcon } from '@chakra-ui/icons'
import { Box, Flex, HStack, IconButton, Tab, TabProps, Text, Tooltip } from '@chakra-ui/react'
import { FormLabelsContext, PopUpMessageContext, useAppModals, useAuth } from '@hb/react'
import {
  ActionButton,
  AssessmentHelpModal,
  ContactFormModal,
  createNewAssessment,
  DeleteButton,
  FormsViewContext,
  LoadingView,
  PageContainer,
  PregnancyProgress,
  ScheduleCallModal,
  SignInToView,
  SolidActionButton,
  StandaloneInput,
  useFormsView,
  useNavigate,
} from '@hb/react-web'
import { deleteAssessment } from '@hb/react-web/src/utils'
import {
  Assessment,
  colors,
  CreateNewAssessmentArgs,
  DropdownField,
  FieldTypes,
  getAssessmentName,
} from '@hb/shared'
import * as React from 'react'
import { useCallback, useContext, useMemo, useState } from 'react'
import { HelpButton } from '../../Help/HelpButton'
import { PatientDataForm } from '../Patients/PatientDataForm'
import { FormsViewAssessment } from './FormsViewAssessment'
import { useFormsViewData } from './hooks'

export const AssessmentTab = ({ assessment, ...props }: TabProps & { assessment: Assessment }) => (
  <Tab
    fontWeight={500}
    fontSize="sm"
    bg="whiteAlpha.800"
    whiteSpace="nowrap"
    _selected={{ bg: colors.green.hex, color: 'white' }}
    _focus={{ boxShadow: 'none' }}
    {...props}>
    {getAssessmentName(assessment)}
  </Tab>
)
const AssessmentSelect = () => {
  const { assessmentId, assessments } = useFormsView()
  const { admin, user } = useAuth()
  const [onCreateLoading, setOnCreateLoading] = useState(false)
  const navigate = useNavigate()

  const mostRecentAssessment = useMemo(
    () =>
      Object.values(assessments ?? {}).sort((a, b) => (b.createdOn ?? 0) - (a.createdOn ?? 0))[0],
    [assessments],
  )
  const canPatientCreateNew = useMemo(() => {
    if (admin) return false
    return !!mostRecentAssessment?.resultsViewedOn && !!mostRecentAssessment.signedOnDate
  }, [admin, mostRecentAssessment])

  const assessmentSelectField = useMemo<DropdownField>(
    () => ({
      placeholder: 'Select a Pregnancy',
      type: FieldTypes.DROPDOWN,
      options:
        Object.entries(assessments ?? {}).map(([id, assessment]) => ({
          id,
          text: getAssessmentName(assessment),
        })) ?? [],
    }),
    [assessments],
  )

  const handleCreateNew = useCallback(async () => {
    if (!user) return
    setOnCreateLoading(true)
    const submitted: CreateNewAssessmentArgs = { patientId: user.id }
    if (mostRecentAssessment?.midwifeId) {
      submitted.practiceId = mostRecentAssessment.midwifeId
    }
    const res = await createNewAssessment(submitted)
    const id = res.data as string | undefined
    setOnCreateLoading(false)
    navigate(`/pregnancy/${id}`)
  }, [user, mostRecentAssessment, navigate])

  return (
    <Flex align="center" flex={1} minW="0" zIndex={2} position="relative">
      <Box flex={1} minW="0">
        <StandaloneInput
          field={assessmentSelectField}
          theme="detailed"
          size="lg"
          onChange={(v: string) => {
            navigate(`/pregnancy/${v}`)
          }}
          value={assessmentId}
        />
      </Box>
      {admin ? (
        <HStack>
          <IconButton
            isLoading={onCreateLoading}
            onClick={handleCreateNew}
            ml={3}
            bg="white"
            size="sm"
            color="gray.500"
            boxShadow="md"
            aria-label="add assessment"
            icon={<AddIcon />}
          />
          {assessmentId ? (
            <Box bg="red.200" borderRadius={6} shadow="sm">
              <DeleteButton
                size="sm"
                itemName="assessment"
                onDelete={() =>
                  deleteAssessment(assessmentId).then(({ success }) => {
                    if (success) {
                      navigate('/pregnancy')
                    }
                  })
                }
              />
            </Box>
          ) : null}
        </HStack>
      ) : (
        <Tooltip
          label={
            canPatientCreateNew
              ? '+ Add New Pregnancy'
              : 'You already have a current pregnancy on file. Please reach out to our team via:\n- The chat in the bottom-right corner of your screen \n- Scheduling a call with the team member you previously spoke to'
          }
          whiteSpace="pre-wrap"
          placement="bottom"
          bg={canPatientCreateNew ? 'green.500' : 'gray.500'}
          hasArrow>
          <Box ml={3}>
            <IconButton
              isLoading={onCreateLoading}
              isDisabled={!canPatientCreateNew}
              onClick={handleCreateNew}
              bg="white"
              size="sm"
              color="gray.500"
              boxShadow="md"
              aria-label="add new pregnancy"
              icon={<AddIcon />}
            />
          </Box>
        </Tooltip>
      )}
    </Flex>
  )
}

const FormsViewBody = () => {
  const emailVerification = useAppModals(s => s.emailVerification)
  const {
    assessmentIndex,
    assessment,
    step,
    selectStep,
    assessments,
    populatedUser: populatedUserData,
    assessmentId,
    assessmentsLoading,
  } = useFormsView()
  const { authUser, loading } = useAuth()
  const { user: populatedUser } = populatedUserData ?? {}
  const { phone, dob, fname, lname } = populatedUser ?? {}
  const { processResponse } = useContext(PopUpMessageContext)
  const navigate = useNavigate()
  // this should come from current assessment
  const [addLoading, setAddLoading] = useState(false)
  const onAddAssessment = useCallback(() => {
    if (!authUser) return processResponse({ error: 'Not signed in' })
    setAddLoading(true)
    const created: CreateNewAssessmentArgs = { patientId: authUser.uid }
    const mostRecentAssessment = Object.values(assessments ?? {}).sort(
      (a, b) => (b.createdOn ?? 0) - (a.createdOn ?? 0),
    )[0]
    const practiceId = mostRecentAssessment?.midwifeId
    if (practiceId) {
      created.practiceId = practiceId
    }
    return createNewAssessment(created)
      .then(({ data }) => {
        if (data) {
          navigate(`/pregnancy/${data}`)
        }
        return processResponse({ success: 'Pregnancy created!' })
      })
      .catch((err: any) => {
        processResponse({
          error: err?.message ?? 'Error creating assessment - get in touch!',
        })
      })
  }, [authUser, processResponse, assessments, navigate])
  const hasAssessments = useMemo(() => Object.keys(assessments ?? {}).length, [assessments])
  const patientDataComplete = useMemo(
    () => phone && dob && fname && lname,
    [phone, dob, fname, lname],
  )

  if (!authUser && !loading) {
    return <SignInToView message="Sign in to get started" />
  }
  if (!authUser || assessmentsLoading || loading || !populatedUser) {
    return <LoadingView text="Loading..." />
  }
  if (!authUser.emailVerified) {
    return (
      <ActionButton
        bg="white"
        onClick={() => {
          emailVerification.open()
        }}>
        Verify your email to get started
      </ActionButton>
    )
  }
  if (!patientDataComplete) {
    return <PatientDataForm />
  }

  if (!hasAssessments) {
    return (
      <SolidActionButton
        size="lg"
        // boxShadow="1px 1px 3px #00000066"
        onClick={onAddAssessment}
        isLoading={addLoading}>
        Begin your Questionnaire
      </SolidActionButton>
    )
  }
  return (
    <>
      <Flex px={[0, 1]} h="100%" zIndex={0} pos="relative" direction="column" width="100%">
        <HStack width="100%">
          <AssessmentSelect />
          <HelpButton />
        </HStack>
        <PregnancyProgress stage={step || 'questions'} selectStage={selectStep} />
        <Flex w="100%" direction="column">
          {assessmentId && assessment.assessmentData ? (
            <FormsViewAssessment
              assessmentId={assessmentId}
              stage={step || 'questions'}
              assessmentIndex={assessmentIndex}
            />
          ) : null}
        </Flex>
      </Flex>
      <AssessmentHelpModal />
      <ContactFormModal id="FormsView" />
      <ScheduleCallModal id="FormsView" />
    </>
  )
}

const FormsView: React.FC = () => {
  const data = useFormsViewData()
  const { assessment } = data ?? {}
  const formLabelsValue = useMemo(() => assessment.populated?.mergedData ?? {}, [assessment])
  return (
    <FormLabelsContext.Provider value={formLabelsValue}>
      <PageContainer>
        <Flex
          py={3}
          w="100%"
          maxW="1000px"
          // width={`${width}px`}
          position="relative"
          direction="column"
          align="center">
          <HStack
            bg={colors.green.hex}
            borderRadius={6}
            py={2}
            px={3}
            mb={3}
            boxShadow="md"
            w="100%">
            <Text
              fontFamily="Comfortaa"
              fontWeight={600}
              fontSize="lg"
              w="100%"
              textAlign={['center', 'center', 'left']}
              color="white"
              textShadow="1px 1px 2px #00000077">
              Pregnancy Coverage Assessment
            </Text>
          </HStack>
          <FormsViewContext.Provider value={data}>
            <FormsViewBody />
          </FormsViewContext.Provider>
        </Flex>
      </PageContainer>
    </FormLabelsContext.Provider>
  )
}

export default FormsView
